import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginTemplateGameData,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Template specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class TemplateGenerationService
	extends BaseGenerationService<PluginTemplateGameData>
{
	readonly pluginType = PluginType.Template;

	/**
	 * Extract text content for auto-generation
	 * @param sourceMaterial The source material to analyze
	 * @param settingsService Settings service for language preferences
	 * @returns Prompts for automatic text extraction
	 */
	extractTextContent(sourceMaterial: any, settingsService: SettingsService): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		const systemPrompt = `Olet opetusmateriaalin kirjoittaja. Tehtäväsi on kirjoittaa ytimekästä opetustekstiä lähdemateriaalin pohjalta. Analysoi lähdemateriaali ja luo siitä selkeä, informatiivinen tekstikooste. Keskity tärkeimpiin asioihin ja käytä selkeää kieltä.

${targetLanguagePrompt}`;

		const includeImages = sourceMaterial?.imagesAsString
			? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna tekstin laatimisessa:\n${sourceMaterial.imagesAsString}`
			: '';

		const userPrompt = `Analysoi annettu lähdemateriaali ja luo siitä yhtenäinen opetusteksti.${includeImages}

Noudata seuraavia ohjeita:
- Luo selkeä, johdonmukainen teksti lähdemateriaalin pohjalta
- Jaa teksti loogisiin alaotsikoihin ja kappaleisiin käyttämällä <h3>- ja <p>-tageja
- Sisällytä tärkeimmät tiedot ja yksityiskohdat lähdemateriaalista
- Varmista, että teksti on opetuksellista ja helposti ymmärrettävää

Palauta JSON-objekti, jossa on seuraavat kentät:
{
	"modifiedText": "Muotoiltu opetusteksti HTML-muodossa alaotsikoilla ja kappaleilla"
}`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for expanding user text with source material
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createTextExpansionPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;
		const editRequest = parameters['editRequest'] || '';

		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		const systemPrompt = `Olet opetusmateriaalin kirjoittaja. Tehtäväsi on kirjoittaa opetustekstiä lähdemateriaalin pohjalta. Lukijan tulee oppia asia kirjoittamasi tekstin pohjalta. Ole siis yksityiskohtainen ja sisällytä mukaan mahdollisimman paljon lähdemateriaalia. Anna vastaus validoidussa JSON-muodossa. Jaa vastaus loogisiin alaotsikoihin ja kappaleisiin käyttämällä <h3>- ja <p>-tageja

${targetLanguagePrompt}`;

		const userPrompt = `Käyttäjän lause on: ${editRequest}.
        
        Tee tiedostohakuja lähdemateriaaliin hakutyökalulla. Yritä löytää lauseeseen liittyviä asioita ja lisää niistä mahdollisimman paljon yksityiskohtia.
        
        Palauta JSON-objekti, jossa on seuraavat kentät:
        - "modifiedText" // (string) Muokattu teksti, joka sisältää käyttäjän antaman lauseen ympärille lisätyt tiedot ja yksityiskohdat.`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create system and user prompts for template generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;

		// For auto-generation, use automatic text extraction
		if (parameters['autoGeneration']) {
			return this.extractTextContent(request.sourceMaterial, settingsService);
		}

		// For text expansion with user input
		if (parameters['textExpansion']) {
			return this.createTextExpansionPrompts(request, settingsService);
		}

		throw new Error('Generation method not yet implemented');
	}

	/**
	 * Process AI response into PluginTemplateGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(response: any, parameters: any): GenerationResult {
		try {
			const modifiedText = response.json?.modifiedText;

			if (!modifiedText || typeof modifiedText !== 'string') {
				return {
					success: false,
					error: 'Invalid response format: no modifiedText found',
					rawResponse: response.rawResponse,
				};
			}

			// Create game data with generated text
			const gameData = new PluginTemplateGameData();
			gameData.modifiedText = modifiedText;
			gameData.selectedArticles = [];
			gameData.selectedPluginType = PluginType.None;

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate template specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		// For text expansion, we need either source material or edit request
		if (request.parameters['textExpansion']) {
			const hasEditRequest = request.parameters['editRequest'] &&
				request.parameters['editRequest'].trim();

			if (!hasSourceMaterial && !hasEditRequest) {
				return {
					valid: false,
					error: 'Anna teksti laajennettavaksi tai valitse lähdemateriaali',
				};
			}
		} else if (!hasSourceMaterial) {
			// For auto-generation, we need source material
			return {
				valid: false,
				error: 'Valitse vähintään yksi artikkeli lähteeksi',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for template generation modal
	 */
	getDefaultParameters(): any {
		return {
			templateTopic: '',
			generationInstructions: '',
			sectionCount: 3,
			clearPrevious: false,
			selectedInterfaceId: 'interface-1', // Will be added when renderer supports interfaces
		};
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: PluginTemplateGameData,
		newGameData: PluginTemplateGameData,
		clearPrevious: boolean,
	): PluginTemplateGameData {
		if (clearPrevious) {
			// Replace all content
			existingGameData.modifiedText = newGameData.modifiedText || '';
		} else {
			// Append new content if it doesn't exist
			if (newGameData.modifiedText && !existingGameData.modifiedText) {
				existingGameData.modifiedText = newGameData.modifiedText;
			}
		}

		return existingGameData;
	}
}